import * as $ from 'jquery';
import * as beautify from 'js-beautify';

import { RowHeight, RowHeights } from '@mode/reports/sandbox/contract';
import { ChartTypes } from '@mode/shared/contract-common';
import { forceResize, getColumnSize, safeParseHTML } from '@mode/shared/util-js';
import { MAX_REPORT_GRID_COLUMNS, ROW_HEIGHT_ATTR, TEXT_BLOCK_MARGINS_CLASS, VIZ_SELECTORS } from '../common/constants';

export interface VizPlacement {
  id: string;
  dir: string;
}

interface VizOptions {
  token: string;
}

export namespace ReportLayoutHelpers {
  function balanceColumns(grid: JQuery) {
    const rows = grid.children('.row');
    rows.each((index, row) => {
      const item = $(row);
      let cols = item.children('div[class^=col-]');
      let total = 0;

      // toArray needed because of the possible element removals
      cols.toArray().forEach((col) => {
        const $col = $(col);
        const colSize = getColumnSize($col);

        //delete all columns that have 0 or fewer columns
        if (colSize <= 0) {
          removeNodeAndEmptyLine($col);
        } else {
          total += colSize;
        }
      });

      cols = item.children('div[class^=col-]');

      if (total !== MAX_REPORT_GRID_COLUMNS) {
        // reset to evenly sized columns
        const size = Math.floor(MAX_REPORT_GRID_COLUMNS / cols.length);
        cols.each((colIndex, col) => {
          col.setAttribute('class', 'col-md-' + size);
        });

        // Incase there is a remainder leftover
        if (size * cols.length !== MAX_REPORT_GRID_COLUMNS) {
          $(row).add(`<div class="col-md-${MAX_REPORT_GRID_COLUMNS % cols.length}"></div>`);
        }
      }
    });
  }

  function removeNodeAndEmptyLine(elem: JQuery, detach = false) {
    const parent = elem.parent();
    const index = parent.contents().index(elem);
    const count = parent.contents().length;

    detach ? elem.detach() : elem.remove();

    // if the node in the same place is now a text node, it's probably a newline
    if (parent.contents()[index] && parent.contents()[index].nodeType === 3) {
      parent.contents().eq(index).remove();
    }
  }

  function clearEmptyRows(grid: JQuery) {
    const rows = grid.children('.row');
    rows.each((index, row) => {
      const item = $(row);
      if (!item.children('div[class^=col-]').length) {
        removeNodeAndEmptyLine(item);
      } else {
        //Have to do this trim check nonsense because blank text counts as a child
        if (!item.children('div[class^=col-]').is((ii, col) => $.trim($(col).html()).length > 0)) {
          removeNodeAndEmptyLine(item);
        }
      }
    });
  }

  function joinDom(DOM: Element[]): string {
    const newHTML = [];
    if (DOM.length) {
      DOM.forEach(function (nodes) {
        if (typeof nodes === 'string') {
          newHTML.push(nodes);
        } else if (nodes.nodeType === 3) {
          newHTML.push(nodes.textContent);
        } else if (nodes.nodeType === 8) {
          // Leave HTML comments on the root level intact
          newHTML.push('<!--' + nodes.textContent + '-->');
        } else {
          newHTML.push(nodes.outerHTML);
        }
      });
    }
    return beautify.html(newHTML.join(''), {
      indent_size: 2,
      //preserve_newlines: false
    });
  }

  export function removeVizDOM(contentNodes, root, id: string) {
    // Note: this can remove elements outside of the mode-grid for compatibility purposes
    const viz = root.find('#' + id);

    if (viz.length) {
      removeNodeAndEmptyLine(viz);
    }

    //Remove all empty rows
    clearEmptyRows(contentNodes.filter('.mode-grid'));
  }

  export function removeViz(layout, id: string): string {
    //Wrapping the HTML as a hack to keep the API consistent
    const safeHTML = safeParseHTML('<div>' + layout + '</div>');
    const DOM = $(safeHTML.body);

    removeVizDOM(DOM.contents(), DOM, id);
    return joinDom(safeHTML.head.concat(DOM.contents().toArray()));
  }

  /**
   *
   * @param contentNodes
   * @param root the root node incase the contentNodes is completely empty
   * @param viz
   * @param chartType the V1 or V2 chart type
   * @param type chart or table
   * @param position -1 to add at the bottom of the report
   * @param placement alternative position option to place the viz relative to another given an id and direction. Overrides position.
   * @returns {void} returns the potentially modified DOM. This is incase there is no mode-grid div, in which case the DOM will be modified to include it
   */
  export function addVizDOM(
    contentNodes,
    root: JQuery,
    viz: VizOptions,
    chartType: ChartTypes.ChartType | ChartTypes.V1ChartType,
    type: string,
    position = -1,
    placement?: VizPlacement
  ) {
    const id = `${type}_${viz.token}`;
    const optionalDataset = type !== 'python' ? 'dataset="dataset"' : '';
    const vizString = viz
      ? `<mode-${type} id="${id}" ${optionalDataset}options="${type}_options"></mode-${type}>`
      : `<mode-${type} dataset="dataset" options="${type}_options"></mode-${type}>`;

    let vizContainerStr = '<div class="row"><div class="col-md-12"></div></div>';
    if (chartType === 'bigNumber') {
      vizContainerStr =
        '<div class="row" data-row-height="small"><div class="col-md-3"></div><div class="col-md-9"></div></div>';
    }
    if (type === 'table') {
      vizContainerStr = `<div class="row" data-row-height="${RowHeights.Default}"><div class="col-md-12"></div></div>`;
    }
    const newRow = $(vizContainerStr);
    const newCol = newRow.children().first();
    newCol.append($(vizString));

    let modeGrid = contentNodes.filter('.mode-grid');
    if (!modeGrid.length) {
      modeGrid = $('<div class="mode-grid container"></div>');
      root.append(modeGrid);
    }

    const rows = contentNodes.filter('.mode-grid').children('.row');

    if (placement) {
      switch (placement.dir) {
        case 'below':
          position = rows.has(`#` + placement.id).index() + 1;
          break;
        case 'above':
          position = rows.has(`#` + placement.id).index();
          break;
      }
    }

    if (rows.length) {
      if (position >= 0) {
        if (position < rows.length) {
          $(rows[position]).before(newRow);
        } else {
          $(rows[rows.length - 1]).after(newRow);
        }
      } else {
        // Auto position rules
        // 1. New tables go on bottom
        // 2. New charts/python go above first table
        // 2a. If no tables in layout, then new chart goes after last chart/python

        const firstTable = modeGrid.find('mode-table').first();
        if (type !== 'table' && firstTable.length) {
          const row = firstTable.parents('.row');
          row.before(newRow);
        } else {
          rows.last().after(newRow);
        }
      }
    } else {
      //Have to create the first row
      modeGrid.append(newRow);
    }
  }

  export function addViz(
    layout: string,
    viz: VizOptions,
    chartType: ChartTypes.V1ChartType | ChartTypes.ChartType,
    type: string,
    position?: number,
    placement?: VizPlacement
  ): string {
    if (!viz || !viz.token) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('viz is empty');
      }
      return layout;
    }

    //Wrapping the HTML as a hack to keep the API consistent
    const safeHTML = safeParseHTML('<div>' + layout + '</div>');
    const DOM = $(safeHTML.body);

    addVizDOM(DOM.contents(), DOM, viz, chartType, type, position, placement);

    return joinDom(safeHTML.head.concat(DOM.contents().toArray()));
  }

  export function updateVizDOM(contentNodes, root, id: string, contents) {
    const viz = root.find('#' + id);
    if (viz.length) {
      viz[0].innerHTML = contents;
    }
  }

  export function updateVizContent(layout: string, id: string, contents: string): string {
    //Wrapping the HTML as a hack to keep the API consistent
    const safeHTML = safeParseHTML('<div>' + layout + '</div>');
    const DOM = $(safeHTML.body);

    updateVizDOM(DOM.children(), DOM, id, contents);
    return joinDom(safeHTML.head.concat(DOM.children().toArray()));
  }

  export function getRows() {
    return document.querySelectorAll('div.mode-grid .row');
  }

  //test if the vizualization passed is alone in row
  //having only one chart in that row
  export function isVizAloneInRow(id: string) {
    const vizRow = document.querySelector(`#${id}`).closest('.row');
    //check the rows columns for direct items
    //the div is the column
    return vizRow.querySelectorAll(':scope > div > *').length === 1;
  }

  export function getCurrentVizIndex(id: string): number {
    const rows = this.getRows();
    let index = null;
    rows.forEach((row, i) => {
      if (row.querySelector(`#${id}`)) {
        index = i;
      }
    });
    return index;
  }

  export function scrollIntoView(selector: string) {
    document.querySelector(selector).scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }

  export function moveGridElementDOM(contentNodes, id: string, element, row: number, column: number) {
    //Make sure it is a Jquery object list
    if (HTMLCollection.prototype.isPrototypeOf(contentNodes)) {
      contentNodes = $(contentNodes);
    }
    const viz = contentNodes.filter('.mode-grid').find('#' + id);
    const rows = contentNodes.filter('.mode-grid').children('.row');

    const sourceColumn = viz.parents('div[class^="col-md-"]');
    if (sourceColumn.length <= 0) {
      return; // maybe someone customized the grid html, but can't continue anyway
    }

    removeNodeAndEmptyLine(viz, true);

    if (row % 2 === 0) {
      //inserting a new row. Fill the row completely.
      const newRow = $('<div class="row"><div class="col-md-12"></div></div>');
      const newCol = newRow.children().first();
      newCol.append(viz);
      if (rows.length * 2 === row) {
        //Inserting at the bottom
        rows.last().after(newRow);
      } else {
        $(rows[row / 2]).before(newRow);
      }
    } else {
      //moving to a different row or within an existing row
      const cols = $(rows[Math.floor(row / 2)]).children();
      const children = $(cols[column]).children();

      if (children.length) {
        //Swap
        removeNodeAndEmptyLine(children, true);
        children.appendTo(sourceColumn);
      }

      viz.appendTo($(cols[column]));

      // If moving a non-textbox into a row containing a textbox with full height, change row height to small
      const rowEl = $(rows[Math.floor(row / 2)]);
      const currentHeight = rowEl.attr(ROW_HEIGHT_ATTR);
      const nonTextSelectors = VIZ_SELECTORS.replace(',mode-text', '');
      if (!currentHeight && rowEl.find('mode-text').length && rowEl.find(nonTextSelectors).length) {
        rowEl.attr(ROW_HEIGHT_ATTR, 'small');
      }
    }

    // Merge empty columns left behind, if any
    if (!sourceColumn.html().trim()) {
      const next = sourceColumn.next();
      const prev = sourceColumn.prev();

      if (next.length && !next.html().trim()) {
        const newSize = getColumnSize(next) + getColumnSize(sourceColumn);
        removeNodeAndEmptyLine(next);
        sourceColumn.removeClass('col-md-' + getColumnSize(sourceColumn));
        sourceColumn.addClass('col-md-' + newSize);
      }

      if (prev.length && !prev.html().trim()) {
        const newSize = getColumnSize(prev) + getColumnSize(sourceColumn);
        removeNodeAndEmptyLine(prev);
        sourceColumn.removeClass('col-md-' + getColumnSize(sourceColumn));
        sourceColumn.addClass('col-md-' + newSize);
      }
    }

    const grid = contentNodes.filter('.mode-grid');
    clearEmptyRows(grid);
    balanceColumns(grid);
  }

  export function moveGridElement(layout: string, id: string, element, row: number, column: number): string {
    const safeHTML = safeParseHTML(layout);
    const DOM = $(safeHTML.body);

    moveGridElementDOM(DOM, id, element, row, column);

    //Rejoin DOM
    return joinDom(safeHTML.head.concat(DOM.toArray()));
  }

  export function setAutoTableDOM(DOM: JQuery, id: string, element) {
    const elem = DOM.find('#' + id);
    const row = elem.parents('.row');
    const col = elem.parents('[class^=col-md]');

    if (col.hasClass('col-md-12')) {
      if (id.indexOf('python') === 0) {
        row.removeAttr(ROW_HEIGHT_ATTR);
      } else {
        row.attr(ROW_HEIGHT_ATTR, 'auto');
      }
    } else {
      const rowIndex = row.parent().children('.row').index(row);
      moveGridElementDOM(DOM, id, element, (rowIndex + 1) * 2, 0);
      if (id.indexOf('python') > 0) {
        elem.parents('.row').attr(ROW_HEIGHT_ATTR, 'auto');
      }
    }
  }

  export function setFullTableDOM(DOM: JQuery, id: string, element) {
    const elem = DOM.find('#' + id);
    const row = elem.parents('.row');
    const col = elem.parents('[class^=col-md]');

    if (col.hasClass('col-md-12')) {
      row.removeAttr(ROW_HEIGHT_ATTR);
    } else {
      const rowIndex = row.parent().children('.row').index(row);
      moveGridElementDOM(DOM, id, element, (rowIndex + 1) * 2, 0);
    }
  }

  /**
   * Maximizes a table. If it is alone in a row, it will make it full width with uncapped height. Otherwise
   * it will move it to a new row.
   *
   * @param layout
   * @param element
   * @param id the table id
   */
  export function setFullTable(layout: string, element, id: string): string {
    const safeHTML = safeParseHTML(layout);
    const DOM = $(safeHTML.body);

    setFullTableDOM(DOM, id, element);

    //Rejoin DOM
    return joinDom(safeHTML.head.concat(DOM.toArray()));
  }

  export function setAutoTable(layout: string, element, id: string): string {
    const safeHTML = safeParseHTML(layout);
    const DOM = $(safeHTML.body);

    setAutoTableDOM(DOM, id, element);

    //Rejoin DOM
    return joinDom(safeHTML.head.concat(DOM.toArray()));
  }

  export function resizeGridElementDOM(DOM, id: string, element, dir, size) {
    const $viz = DOM.find('#' + id);
    const col = $viz.parentsUntil('div.row').last();
    const current = getColumnSize(col);
    const diff = current - size; //Diff will be negative if we're growing the element.

    if (current === 12 && id.indexOf('table') === 0) {
      // If we're shrinking a full-sized table, set it to large row height
      col.parent().attr(ROW_HEIGHT_ATTR, 'large');
    }

    if (id.indexOf('text') === 0) {
      // text elements can only have margins if 12 columns, so if resizing, probably should remove it
      $viz.removeClass('text-margins');
    }

    switch (dir) {
      case 'left':
        col.removeClass();
        col.addClass('col-md-' + size);

        var prev = col.prev();

        if (prev.length) {
          if (prev.children().length) {
            col.before('<div class="col-md-' + diff + '"></div>');
          } else {
            const prevSize = getColumnSize(prev);
            if (prevSize + diff > 0) {
              prev.removeClass();
              prev.addClass('col-md-' + (prevSize + diff));
            } else {
              removeNodeAndEmptyLine(prev);
            }
          }
        } else {
          //Probably need to add a new column
          col.before('<div class="col-md-' + diff + '"></div>');
        }

        break;
      default:
        col.removeClass();
        col.addClass('col-md-' + size);

        var next = col.next();

        if (next.length) {
          if (next.children().length) {
            col.after('<div class="col-md-' + diff + '"></div>');
          } else {
            const nextSize = getColumnSize(next);
            if (nextSize + diff > 0) {
              next.removeClass();
              next.addClass('col-md-' + (nextSize + diff));
            } else {
              removeNodeAndEmptyLine(next);
            }
          }
        } else {
          //Probably need to add a new column
          col.after('<div class="col-md-' + diff + '"></div>');
        }
        break;
    }
  }

  /**
   * Resizes a grid element in a report. Modification is done in place.
   *
   * @param layout
   * @param id the html ID of the visualization
   * @param element unused
   * @param type the visualization type, i.e. 'chart' or 'table'
   * @param dir direction of resize. 'left' if resizing from the left handle, or 'right' otherwise.
   * @param size number of columns of the new size, as an integer
   */
  export function resizeGridElement(layout: string, id: string, element, dir: string, size: number): string {
    const safeHTML = safeParseHTML(layout);
    const DOM = $(safeHTML.body);

    resizeGridElementDOM(DOM, id, element, dir, size);

    //Rejoin DOM
    return joinDom(safeHTML.head.concat(DOM.toArray()));
  }

  export function resizeRowHeightDOM(contentNodes: JQuery, rowIndex: number, size: RowHeight) {
    const rows = contentNodes.filter('.mode-grid').children('.row');

    if (rows.length > rowIndex && rowIndex >= 0) {
      const row = $(rows[rowIndex]);
      const nonTextSelectors = VIZ_SELECTORS.replace(',mode-text', '');
      const $nonTextboxElements = row.find(nonTextSelectors);

      // Full means clear the row height. To be safe, check if it can be made full.
      // Textboxes can be set to full if and only if there are only textboxes in the row.
      if (size === 'full') {
        if ($nonTextboxElements.length === 0) {
          row.removeAttr(ROW_HEIGHT_ATTR);
          return;
        }
      } else {
        row.find('mode-text').removeClass('text-margins'); // margins only for full-height text elements
        row.attr(ROW_HEIGHT_ATTR, size);
      }
    }
  }

  /**
   * Resizes a row's height
   *
   * @param layout
   * @param element
   * @param rowIndex The index of the row, starting from 0
   * @param size The size multiplier for the row. Valid values are 1, 2 & 4
   */
  export function resizeRowHeight(layout: string, id: string, size: RowHeight, isPythonGraph = false): string {
    const safeHTML = safeParseHTML(layout);
    const DOM = $(safeHTML.body);

    const $viz = DOM.find('#' + id);
    if ($viz.length) {
      const $row = $viz.parents('.row');
      resizeRowHeightDOM(DOM, $row.parent().children('.row').index($row), size);
    }

    if (isPythonGraph) {
      forceResize();
    }

    //Rejoin DOM
    return joinDom(safeHTML.head.concat(DOM.toArray()));
  }

  export function toggleTextMarginsDOM(DOM: JQuery, id: string, element) {
    const elem = DOM.find('#' + id);
    elem.toggleClass(TEXT_BLOCK_MARGINS_CLASS);
  }

  export function toggleTextMargins(layout: string, element, id): string {
    const safeHTML = safeParseHTML(layout);
    const DOM = $(safeHTML.body);

    toggleTextMarginsDOM(DOM, id, element);

    return joinDom(safeHTML.head.concat(DOM.toArray()));
  }

  export function applyTheme($window: angular.IWindowService, baseHref: string, customSource: string) {
    if (baseHref) {
      const theme = $window.document.head.querySelector('#builtin-theme');
      if (theme != null) {
        theme.setAttribute('href', baseHref);
      }
    }

    const custom = $window.document.head.querySelector('#custom-theme');
    if (custom != null) {
      // need to be able to clear the custom CSS if switching to a theme without any
      custom.innerHTML = customSource || '';
    }
  }
}
